import type { StoryEditAction, StoryEditActionResult } from 'common/story-edit-operations/types';

export const MAX_ACTIONS_PER_REQUEST = 100;

/**
 * Batches the captured edit actions of one story for the server. At most one batch is in flight: its actions
 * leave the unsent queue until the response acknowledges them, a retryable failure releases them back ahead of
 * newer actions, and a rejected batch is dropped.
 */
export class StoryEditSaveQueue {
	private inFlight: StoryEditAction[] = [];
	private unsent: StoryEditAction[] = [];

	enqueue(action: StoryEditAction) {
		this.unsent.push(action);
	}

	/** Moves up to 100 unsent actions in flight; undefined while a batch is in flight or nothing is unsent. */
	startBatch(): StoryEditAction[] | undefined {
		if (this.isInFlight || !this.hasUnsent) return undefined;
		this.inFlight = this.unsent.splice(0, MAX_ACTIONS_PER_REQUEST);
		return [...this.inFlight];
	}

	/** Removes in-flight actions acknowledged as applied or duplicate; ends the batch. */
	acknowledge(results: readonly StoryEditActionResult[]) {
		const acknowledgedIds = new Set(results.map(result => result.actionId));
		const unacknowledged = this.inFlight.filter(action => !acknowledgedIds.has(action.actionId));
		this.inFlight = [];
		this.unsent = [...unacknowledged, ...this.unsent];
	}

	/** Retryable failure: the in-flight batch goes back ahead of newer unsent actions. */
	release() {
		this.unsent = [...this.inFlight, ...this.unsent];
		this.inFlight = [];
	}

	/** Rejected batch (400/413/403/404): drops and returns it. */
	drop(): StoryEditAction[] {
		const dropped = this.inFlight;
		this.inFlight = [];
		return dropped;
	}

	/** In-flight then unsent, in capture order. */
	get pending(): readonly StoryEditAction[] {
		return [...this.inFlight, ...this.unsent];
	}

	get hasUnsent() {
		return this.unsent.length > 0;
	}

	get isInFlight() {
		return this.inFlight.length > 0;
	}

	get isIdle() {
		return !this.isInFlight && !this.hasUnsent;
	}
}
